import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab } from '@mui/material';

// Stock History workspace - NAVIGATION ONLY.
//
// Two views of the same ledger, as a small tab strip under the Inventory
// module tabs: the HISTORY (every movement, the page that already existed)
// and ITEM PROFIT (per store item - what was bought, what is left, what was
// sold from it and whether it made money). Every tab navigates to an
// existing route; the pages, their data and their permissions are untouched.
//
// Same shape as RestroReportsLayout on purpose - one tab-strip pattern in the
// product. Both tabs share the Stock History permission key: whoever may read
// the ledger may read what it adds up to, and a key of its own would have to
// be granted to every existing user before the tab appeared for anybody.
const TABS = [
  {
    label: 'History',
    path: '/admin/StockTransactionHistory',
    menuKey: 'StockTransactionHistory',
    exact: true,
  },
  {
    label: 'Item Profit',
    path: '/admin/StockTransactionHistory/ItemProfit',
    menuKey: 'StockTransactionHistory',
  },
];

// Same visibility rule as the sidebar: master admin sees all, otherwise the
// stored permission grants decide; fail-open when no permission list exists.
const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return TABS;
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return TABS.filter((t) => keys.has(t.menuKey));
  } catch (_) {
    return TABS;
  }
};

// For the header search - the same filter the strip uses.
export const allowedStockHistoryTabs = () => allowedTabs();

export default function StockHistoryLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '').toLowerCase();

  // "History" is the parent path of "Item Profit", so it matches only
  // exactly; the child matches itself and anything under it.
  const activeIdx = tabs.findIndex((t) => {
    const p = t.path.toLowerCase();
    return t.exact ? current === p : current === p || current.startsWith(p + '/');
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          px: { xs: 0.5, sm: 1 },
          mb: 0.5,
          borderBottom: '1px solid var(--hd-border, #e2e8f0)',
        }}>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => navigate(tabs[idx].path)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 38,
            '& .MuiTab-root': {
              minHeight: 38,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* The chosen page renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}
